import React from 'react';
import {UserListItem} from '@/common-pages/invitation/invitation.type';
import {ReactComponent as UnreachIcon} from '@/assets/icons/invitation/unreach.svg';
import {ReactComponent as ReachedIcon} from '@/assets/icons/invitation/reached.svg';

// 双排进度条组件
interface DoubleRowProgressProps {
  topRow: UserListItem[];
  bottomRow: UserListItem[];
  reachedCount: number; // 总的已完成项数
  renderTopContent?: (item: UserListItem, index: number) => React.ReactNode;
  renderBottomContent?: (item: UserListItem, index: number) => React.ReactNode;
}

const DoubleRowProgress: React.FC<DoubleRowProgressProps> = ({
  topRow,
  bottomRow,
  reachedCount,
  renderTopContent,
  renderBottomContent,
}) => {
  const containerWidth = 311; // 295 + 16
  const containerHeight = 80; // 双排容器高度
  const strokeWidth = 6;
  const topY = strokeWidth; // 上排Y坐标
  const bottomY = containerHeight - strokeWidth; // 下排Y坐标
  const rightX = containerWidth - strokeWidth; // 右边X坐标
  const cornerRadius = 12; // 圆角半径，增大以提供更好的视觉效果

  // 点位计算的基准宽度（保持原来的295宽度分布）
  const pointsBaseWidth = 295 - strokeWidth; // 保持原来的点位分布宽度
  const pointsFirstCornerX = pointsBaseWidth - cornerRadius; // 用于点位计算
  const pointsSecondCornerX = pointsBaseWidth - cornerRadius; // 用于点位计算

  // 路径绘制的坐标（使用新的311宽度）
  const pathFirstCornerX = rightX - cornerRadius; // 用于路径绘制
  const pathSecondCornerX = rightX - cornerRadius; // 用于路径绘制

  // 计算路径：向右 -> 转角 -> 竖直 -> 转角 -> 向左
  // 第一个转角：从右转向下
  const firstCornerY = topY + cornerRadius;

  // 竖直段
  const verticalStartY = firstCornerY;
  const verticalEndY = bottomY - cornerRadius;

  const pathData = `M 0,${topY} 
    L ${pathFirstCornerX},${topY} 
    Q ${rightX},${topY} ${rightX},${firstCornerY}
    L ${rightX},${verticalEndY}
    Q ${rightX},${bottomY} ${pathSecondCornerX},${bottomY}
    L 0,${bottomY}`;

  // 计算实际路径长度
  // 水平段1 + 第一个转角(1/4圆) + 竖直段 + 第二个转角(1/4圆) + 水平段2
  const horizontalSegment1 = pathFirstCornerX;
  const horizontalSegment2 = pathSecondCornerX;
  const verticalSegment = verticalEndY - verticalStartY;
  const cornerArcLength = (Math.PI * cornerRadius) / 2; // 1/4圆的弧长

  const pathLength =
    horizontalSegment1 +
    cornerArcLength +
    verticalSegment +
    cornerArcLength +
    horizontalSegment2;

  // 内部进度计算
  // 计算上下排各自完成了多少项（暂时保留，可能用于未来扩展）
  // const topReachedCount = Math.min(reachedCount, topRow.length);
  // const bottomReachedCount = Math.max(0, reachedCount - topRow.length);

  // 上排进度（暂时保留，可能用于未来扩展）
  // const topProgress = topRow.length > 1 && topReachedCount > 0
  //   ? (topReachedCount - 1) / (topRow.length - 1)
  //   : 0;

  // 下排进度（暂时保留，可能用于未来扩展）
  // const bottomProgress = bottomRow.length > 1 && bottomReachedCount > 0
  //   ? (bottomReachedCount - 1) / (bottomRow.length - 1)
  //   : 0;

  // 计算已走过的实际路径长度
  let completedPathLength = 0;

  if (reachedCount > 0) {
    const topReachedCount = Math.min(reachedCount, topRow.length);
    const bottomReachedCount = Math.max(0, reachedCount - topRow.length);

    if (topReachedCount > 0) {
      // 上排点的路径：沿上排水平段前进
      // 第i个点位置: (i-1)/(topRow.length-1) * pointsFirstCornerX
      const topProgress =
        topRow.length > 1 ? (topReachedCount - 1) / (topRow.length - 1) : 1;
      completedPathLength = topProgress * pointsFirstCornerX;
    }

    if (bottomReachedCount > 0) {
      // 下排未完成的部分
      const bottomRemainingCount = bottomRow.length - bottomReachedCount;
      const bottomRemainingProgress =
        bottomRow.length > 1
          ? bottomRemainingCount / (bottomRow.length - 1)
          : 0;
      const bottomRemainingLength =
        bottomRemainingProgress * pointsSecondCornerX;

      // 实际完成的进度 = 总进度 - 未完成进度
      completedPathLength = pathLength - bottomRemainingLength;
    }
  }

  const totalProgress = completedPathLength / pathLength;

  return (
    <div className="pl-7 pr-3 py-3 flex flex-col gap-1">
      <div className="relative">
        {/* SVG层 */}
        <svg
          className="w-full h-[5rem]"
          preserveAspectRatio="none"
          viewBox={`0 0 ${containerWidth} ${containerHeight}`}>
          {/* 背景轨道 */}
          <path
            d={pathData}
            stroke="#CCCCCC"
            strokeWidth={strokeWidth}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* 进度填充 */}
          <path
            d={pathData}
            stroke="#AC8E00"
            strokeWidth={strokeWidth}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={pathLength}
            strokeDashoffset={pathLength * (1 - totalProgress)}
          />
        </svg>

        {/* 点位层 - 绝对定位，覆盖在SVG上 */}
        <div className="absolute inset-0">
          {/* 上排点和内容 */}
          {topRow.map((item, i) => {
            // 先计算311x80坐标系下的绝对位置
            const x = (i / (topRow.length - 1)) * pointsFirstCornerX;
            // 转换为百分比
            const leftPercent = (x / containerWidth) * 100;
            const topPercent = (topY / containerHeight) * 100;
            const isReached =
              item.invitedNumber >= item.inviteNumber && item.status === 1;

            return (
              <div
                key={`top-${i}`}
                className="absolute"
                style={{
                  left: `${leftPercent}%`,
                  top: `${topPercent}%`,
                }}>
                {/* 上方内容 */}
                {renderTopContent && (
                  <div
                    className="absolute bottom-full mb-1.5 -translate-x-1/2"
                    style={{
                      left: `${leftPercent}%`,
                    }}>
                    {renderTopContent(item, i)}
                  </div>
                )}

                {/* 中心点图标 */}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                  {isReached ? (
                    <ReachedIcon className="size-3" />
                  ) : (
                    <UnreachIcon className="size-3" />
                  )}
                </div>

                {/* 下方内容 */}
                {renderBottomContent && (
                  <div
                    className="absolute top-full mt-1.5 -translate-x-1/2"
                    style={{
                      left: `${leftPercent}%`,
                    }}>
                    {renderBottomContent(item, i)}
                  </div>
                )}
              </div>
            );
          })}

          {/* 下排点和内容 - 从右到左 */}
          {bottomRow.map((item, i) => {
            // 先计算311x80坐标系下的绝对位置
            const x =
              pointsSecondCornerX -
              (i / (bottomRow.length - 1)) * pointsSecondCornerX;
            // 转换为百分比
            const leftPercent = (x / containerWidth) * 100;
            const topPercent = (bottomY / containerHeight) * 100;
            const isReached =
              item.invitedNumber >= item.inviteNumber && item.status === 1;

            return (
              <div
                key={`bottom-${i}`}
                className="absolute"
                style={{
                  left: `${leftPercent}%`,
                  top: `${topPercent}%`,
                }}>
                {/* 上方内容 */}
                {renderTopContent && (
                  <div
                    className="absolute bottom-full mb-1.5 -translate-x-1/2"
                    style={{
                      left: `${leftPercent}%`,
                    }}>
                    {renderTopContent(item, i)}
                  </div>
                )}

                {/* 中心点图标 */}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                  {isReached ? (
                    <ReachedIcon className="size-3" />
                  ) : (
                    <UnreachIcon className="size-3" />
                  )}
                </div>

                {/* 下方内容 */}
                {renderBottomContent && (
                  <div
                    className="absolute top-full mt-1.5 -translate-x-1/2"
                    style={{
                      left: `${leftPercent}%`,
                    }}>
                    {renderBottomContent(item, i)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DoubleRowProgress;
